/**
 * File Operation Helper Functions
 *
 * Shared utilities for file mutation operations
 */

import { toast } from '@/lib/toast';
import { DirectApiError, handleMutationError } from '@/lib/api/direct-client';
import { MAX_FILE_SIZE } from './constants';
import {
  acceptedExtensionsFor,
  uploadableMimeTypesFor
} from '@/lib/constants/file-type-accept';
import { EMPTY_FILE_MESSAGE } from '@/lib/constants/file-limits';

/**
 * Creates a standardized error handler for file operations
 * @param operation - The operation being performed (e.g., 'delete', 'upload')
 * @returns Error handler function
 */
export const createFileErrorHandler =
  (operation: string) => (error: unknown) => {
    let message = `Failed to ${operation} document`;

    if (error instanceof Error) {
      // Use the error message if it's specific
      if (error.message && !error.message.includes('Failed to')) {
        message = error.message;
      }
    } else if (error instanceof DirectApiError) {
      message = handleMutationError(error);
    }

    toast.error(message);
    console.error(`${operation} file error:`, error);
  };

/**
 * Validates a file before upload, against what the backend actually accepts for `fileType`.
 *
 * **The check is per file type, not global.** It used to test every upload against one
 * shared list regardless of destination, which was wrong in both directions: it passed a
 * `.txt` for any type (only `unknown-pending-ai-analysis` accepts `text/plain`, so it was
 * refused after the S3 PUT), and its hand-kept extension fallback still listed `gif` and
 * `txt` long after LF-4764 dropped them — so a GIF failed the MIME test, passed the
 * extension test, uploaded, and was rejected by the server (VALE-209).
 *
 * The extension fallback stays, because a browser genuinely does report an empty or generic
 * `file.type` for some formats, but it is now derived from the same contract as the MIME
 * check and so cannot outlive it.
 *
 * @param file - File to validate
 * @param fileType - The backend file-type slug this upload is destined for
 * @throws Error if file is invalid
 */
export const validateFile = (file: File, fileType: string): void => {
  if (file.size === 0) {
    throw new Error(EMPTY_FILE_MESSAGE);
  }

  if (file.size > MAX_FILE_SIZE) {
    throw new Error(
      `File size exceeds ${MAX_FILE_SIZE / (1024 * 1024)}MB limit`
    );
  }

  const allowedMimeTypes = uploadableMimeTypesFor(fileType);

  // An unrecognised slug is not this function's to reject — the presign call names it and the
  // server answers for it. Validating against an empty list would refuse every file instead.
  if (allowedMimeTypes.length === 0 || allowedMimeTypes.includes(file.type)) {
    return;
  }

  const extension = file.name.split('.').pop()?.toLowerCase();
  const allowedExtensions = acceptedExtensionsFor(fileType);

  if (!extension || !allowedExtensions.includes(`.${extension}`)) {
    throw new Error(
      `File type not supported. Accepted formats: ${allowedExtensions.join(', ')}.`
    );
  }
};

/**
 * Determines if an error should be retried
 * @param failureCount - Number of previous failures
 * @param error - The error that occurred
 * @returns Whether to retry the operation
 */
export const shouldRetry = (failureCount: number, error: unknown): boolean => {
  if (failureCount >= 3) return false;

  if (error instanceof DirectApiError) {
    // Retry on server errors and network issues
    return error.status >= 500 || error.status === 0;
  }

  return false;
};
